import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { requestsApi, feasibilityApi, dashboardsApi } from '../lib/api';
import { BrandLoader } from '../components/BrandLoader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { FileText, CheckCircle2, Clock, TrendingUp } from 'lucide-react';
import { getStatusColor, getStatusLabel, formatDate } from '../lib/utils';

export default function Feasibility() {
  const { user, isConsultant } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isConsultant()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, statsRes] = await Promise.all([
        requestsApi.getAll({ type: 'FEA' }),
        dashboardsApi.getConsultant()
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Feasibility Studies">
        <div className="flex items-center justify-center h-64">
          <BrandLoader size="md" label="Loading feasibility studies…" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Feasibility Studies">
      <div className="space-y-6" data-testid="feasibility-page">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">My Feasibility Studies</h2>
          <p className="text-slate-500 mt-1">Review and respond to assigned feasibility requests</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_assigned}</p>
                    <p className="text-xs text-slate-500">Total Assigned</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.completed}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
                    <p className="text-xs text-slate-500">Pending</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.completion_rate?.toFixed(0)}%</p>
                    <p className="text-xs text-slate-500">Completion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Requests List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope']">Assigned Studies</CardTitle>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No feasibility studies assigned to you</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <div 
                    key={request.id}
                    className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 cursor-pointer transition-colors"
                    onClick={() => navigate(`/requests/${request.id}`)}
                    data-testid={`fea-request-${request.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-slate-500">{request.ref_no}</span>
                          <Badge className={getStatusColor(request.status)}>
                            {getStatusLabel(request.status, request.type)}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-slate-900">{request.title}</h4>
                        <p className="text-sm text-slate-500 mt-1">
                          {request.specialty_name} • Created {formatDate(request.created_at)}
                        </p>
                      </div>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
